<script>
  // Material Design 3 outlined select with a custom menu panel.
  // The trigger is rendered like an M3 outlined text field; clicking it opens
  // a positioned menu (elevation 2, 4px corner, list items with state layers).
  //
  // Items are passed via the `options` prop:  [{ value, label, supporting? }]
  // The native <select> is intentionally NOT used because the browser's
  // dropdown is not M3 -- this gives us full styling control.
  import { onMount, tick } from 'svelte';

  let {
    label,
    value = $bindable(''),
    options = [],
    onchange = null,
    dense = false, // compact variant for inline use (table rows, toolbars)
  } = $props();

  let open = $state(false);
  let trigger = $state(null);
  let menu = $state(null);
  let menuStyle = $state('');
  let activeIndex = $state(-1);

  const selected = $derived(options.find((o) => o.value === value));
  const id = `sel-${Math.random().toString(36).slice(2, 9)}`;

  async function toggle() {
    if (open) { close(); return; }
    open = true;
    activeIndex = Math.max(0, options.findIndex((o) => o.value === value));
    await tick();
    position();
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
  }

  function close() {
    open = false;
    document.removeEventListener('mousedown', onDocMouseDown);
    document.removeEventListener('keydown', onKey);
  }

  function position() {
    if (!trigger || !menu) return;
    const r = trigger.getBoundingClientRect();
    menuStyle = `top:${r.bottom + 4}px; left:${r.left}px; min-width:${r.width}px;`;
  }

  function onDocMouseDown(e) {
    if (trigger?.contains(e.target) || menu?.contains(e.target)) return;
    close();
  }

  function onKey(e) {
    if (e.key === 'Escape') { close(); trigger?.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); activeIndex = Math.min(options.length - 1, activeIndex + 1); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); activeIndex = Math.max(0, activeIndex - 1); }
    else if (e.key === 'Enter')     { e.preventDefault(); pick(options[activeIndex]?.value); }
  }

  function pick(v) {
    if (v === undefined) return;
    value = v;
    close();
    onchange?.({ target: { value: v } });
  }

  onMount(() => () => close());
</script>

<div class="md-sel" class:dense>
  <button
    bind:this={trigger}
    {id}
    type="button"
    class="md-sel-trigger"
    class:open
    class:dense
    onclick={toggle}
    aria-haspopup="listbox"
    aria-expanded={open}
  >
    <span class="md-sel-text" class:placeholder={!selected}>
      {selected?.label ?? ' '}
    </span>
    {#if label && !dense}<span class="md-sel-label">{label}</span>{/if}
    <span class="material-symbols-outlined md-sel-chevron" class:open>arrow_drop_down</span>
  </button>

  {#if open}
    <ul
      bind:this={menu}
      class="md-sel-menu"
      role="listbox"
      style={menuStyle}
    >
      {#each options as opt, i (opt.value)}
        <li
          role="option"
          aria-selected={opt.value === value}
          class="md-sel-item"
          class:active={i === activeIndex}
          class:selected={opt.value === value}
          onmouseenter={() => (activeIndex = i)}
          onclick={() => pick(opt.value)}
        >
          <span class="md-sel-item-label">{opt.label}</span>
          {#if opt.supporting}<span class="md-sel-item-supporting">{opt.supporting}</span>{/if}
          {#if opt.value === value}<span class="material-symbols-outlined md-sel-check">check</span>{/if}
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .md-sel { position: relative; display: block; width: 100%; }

  .md-sel-trigger {
    width: 100%;
    height: 56px;
    padding: 0 44px 0 16px;
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 4px;
    color: rgb(var(--md-sys-color-on-surface));
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    cursor: pointer;
    position: relative;
    font-family: inherit;
    transition: border-color 150ms, border-width 150ms;
  }
  @media (max-width: 768px) {
    .md-sel-trigger { height: 48px; font-size: 14px; }
    .md-sel-text { line-height: 48px; }
  }
  .md-sel-trigger:hover { border-color: rgb(var(--md-sys-color-on-surface)); }
  .md-sel-trigger:focus, .md-sel-trigger.open {
    outline: none;
    border-color: rgb(var(--md-sys-color-primary));
    border-width: 2px;
    padding: 0 43px 0 15px;
  }
  .md-sel-text { display: block; line-height: 56px; }
  .md-sel-text.placeholder { color: transparent; }

  /* Dense variant for inline use (table rows). 32px tall, no floating label,
     visible value sits flush. Matches the height of compact action buttons. */
  .md-sel.dense { width: auto; display: inline-block; }
  .md-sel-trigger.dense {
    height: 32px;
    padding: 0 32px 0 12px;
    font-size: 13px;
    border-radius: 16px;
    background: rgb(var(--md-sys-color-surface-container));
    border-color: rgb(var(--md-sys-color-outline-variant));
  }
  .md-sel-trigger.dense .md-sel-text { line-height: 30px; }
  .md-sel-trigger.dense:focus,
  .md-sel-trigger.dense.open {
    padding: 0 31px 0 11px;
  }
  .md-sel-trigger.dense .md-sel-chevron { right: 6px; font-size: 18px; }

  .md-sel-label {
    position: absolute;
    left: 12px;
    top: 0;
    transform: translateY(-50%);
    padding: 0 4px;
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 12px;
    line-height: 1;
    pointer-events: none;
  }
  .md-sel-trigger:focus ~ .md-sel-label,
  .md-sel-trigger.open ~ .md-sel-label { color: rgb(var(--md-sys-color-primary)); }

  .md-sel-chevron {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgb(var(--md-sys-color-on-surface-variant));
    pointer-events: none;
    transition: transform 200ms;
  }
  .md-sel-chevron.open { transform: translateY(-50%) rotate(180deg); }

  /* M3 menu panel: surface-container-high background, 4px corner, elevation 2 */
  .md-sel-menu {
    position: fixed;
    z-index: 1000;
    margin: 0;
    padding: 8px 0;
    list-style: none;
    background: rgb(var(--md-sys-color-surface-container-high));
    color: rgb(var(--md-sys-color-on-surface));
    border-radius: 4px;
    box-shadow:
      0 1px 2px 0 rgba(0, 0, 0, 0.30),
      0 2px 6px 2px rgba(0, 0, 0, 0.15);
    max-height: 300px;
    overflow-y: auto;
  }

  .md-sel-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
    height: 48px;
    cursor: pointer;
    position: relative;
  }
  .md-sel-item:hover,
  .md-sel-item.active {
    background: rgb(var(--md-sys-color-on-surface) / 0.08); /* state layer hover 8% */
  }
  .md-sel-item.selected {
    background: rgb(var(--md-sys-color-primary) / 0.12);   /* state layer selected 12% */
  }
  .md-sel-item-label    { flex: 1; }
  .md-sel-item-supporting {
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .md-sel-check {
    color: rgb(var(--md-sys-color-primary));
    font-size: 20px;
  }
</style>
